/**
 * TIMELESS DISPLAY — Product Specifications Accordion
 * Handles accordion expand/collapse functionality
 * Only one item can be expanded at a time
 */

class ProductSpecificationsAccordion {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('[data-accordion-item]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    if (!this.items.length) return;

    // Initialize default open items
    this.items.forEach((item) => {
      const header = item.querySelector('.product-specifications__header');
      const content = item.querySelector('.product-specifications__content');

      if (!header || !content) return;

      // Bind click event
      header.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(item);
      });

      // Bind keyboard events
      header.addEventListener('keydown', (e) => {
        this.handleKeydown(e, item);
      });

      // Set initial state based on data attribute
      const isDefaultOpen = item.dataset.defaultOpen === 'true';
      if (isDefaultOpen) {
        this.expand(item, false); // Don't animate on initial load
      } else {
        this.collapse(item, false);
      }
    });

    // Listen for reduced motion preference changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      this.prefersReducedMotion = e.matches;
    });
  }

  /**
   * Toggle accordion item open/closed
   */
  toggle(item) {
    const header = item.querySelector('.product-specifications__header');
    const isExpanded = header.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.collapse(item);
    } else {
      // Close all other items first (accordion behavior)
      this.closeAllExcept(item);
      this.expand(item);
    }
  }

  /**
   * Expand an accordion item
   */
  expand(item, animate = true) {
    const header = item.querySelector('.product-specifications__header');
    const content = item.querySelector('.product-specifications__content');

    if (!header || !content) return;

    header.setAttribute('aria-expanded', 'true');

    if (animate && !this.prefersReducedMotion) {
      // Animate expand
      content.hidden = false;
      content.style.maxHeight = '0';
      content.style.opacity = '0';

      // Force reflow
      content.offsetHeight;

      // Animate to full height
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.opacity = '1';

      // Clean up after animation
      const handleTransitionEnd = () => {
        content.style.maxHeight = '';
        content.removeEventListener('transitionend', handleTransitionEnd);
      };
      content.addEventListener('transitionend', handleTransitionEnd);
    } else {
      // No animation
      content.hidden = false;
      content.style.maxHeight = '';
      content.style.opacity = '';
    }
  }

  /**
   * Collapse an accordion item
   */
  collapse(item, animate = true) {
    const header = item.querySelector('.product-specifications__header');
    const content = item.querySelector('.product-specifications__content');

    if (!header || !content) return;

    header.setAttribute('aria-expanded', 'false');

    if (animate && !this.prefersReducedMotion) {
      // Set current height explicitly for animation
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.opacity = '1';

      // Force reflow
      content.offsetHeight;

      // Animate to closed
      content.style.maxHeight = '0';
      content.style.opacity = '0';

      // Hide after animation
      const handleTransitionEnd = () => {
        content.hidden = true;
        content.style.maxHeight = '';
        content.style.opacity = '';
        content.removeEventListener('transitionend', handleTransitionEnd);
      };
      content.addEventListener('transitionend', handleTransitionEnd);
    } else {
      // No animation
      content.hidden = true;
      content.style.maxHeight = '';
      content.style.opacity = '';
    }
  }

  /**
   * Close all items except the specified one
   */
  closeAllExcept(exceptItem) {
    this.items.forEach((item) => {
      if (item !== exceptItem) {
        const header = item.querySelector('.product-specifications__header');
        const isExpanded = header && header.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          this.collapse(item);
        }
      }
    });
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(event, item) {
    const key = event.key;
    const headers = Array.from(this.container.querySelectorAll('.product-specifications__header'));
    const currentIndex = headers.indexOf(event.target);

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusHeader(headers, currentIndex + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusHeader(headers, currentIndex - 1);
        break;

      case 'Home':
        event.preventDefault();
        this.focusHeader(headers, 0);
        break;

      case 'End':
        event.preventDefault();
        this.focusHeader(headers, headers.length - 1);
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle(item);
        break;
    }
  }

  /**
   * Focus a header by index (with wrap-around)
   */
  focusHeader(headers, index) {
    if (index < 0) {
      index = headers.length - 1;
    } else if (index >= headers.length) {
      index = 0;
    }
    headers[index].focus();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const accordions = document.querySelectorAll('[data-accordion]');
  accordions.forEach((accordion) => {
    new ProductSpecificationsAccordion(accordion);
  });
});

// Re-initialize on section load (Theme Editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const accordion = event.target.querySelector('[data-accordion]');
    if (accordion) {
      new ProductSpecificationsAccordion(accordion);
    }
  });
}
